import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Eye, Tag, MessageCircle, Send, Home, Settings } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogArticle = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [commentForm, setCommentForm] = useState({
    author_name: '',
    author_email: '',
    content: ''
  });
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    fetchArticle();
    checkAdminStatus();
  }, [slug]);

  const checkAdminStatus = () => {
    const token = localStorage.getItem('admin_token');
    setIsAdmin(!!token);
  };

  const fetchArticle = async () => {
    try {
      const response = await axios.get(`${API}/blog/articles/${slug}`);
      setArticle(response.data);
    } catch (error) {
      console.error('Erreur chargement article:', error);
      toast.error('Article non trouvé');
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    setSubmittingComment(true);

    try {
      await axios.post(`${API}/blog/articles/${article.id}/comments`, commentForm);
      toast.success('Commentaire ajouté avec succès ! Il sera publié après modération.');
      setCommentForm({ author_name: '', author_email: '', content: '' });
      fetchArticle();
    } catch (error) {
      console.error('Erreur ajout commentaire:', error);
      toast.error('Erreur lors de l\'ajout du commentaire');
    } finally {
      setSubmittingComment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F5F5F5' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#335C2E' }}></div>
          <p className="mt-4" style={{ color: '#666666' }}>Chargement...</p>
        </div>
      </div>
    );
  }

  if (!article) return null;

  const approvedComments = article.comments?.filter(c => c.status === 'approved') || [];

  return (
    <div className="min-h-screen" style={{ background: '#F5F5F5' }}>
      {/* Bouton Admin flottant si connecté */}
      {isAdmin && (
        <div className="fixed top-4 right-4 z-50">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg shadow-lg hover:opacity-90 transition-all"
            style={{ background: '#335C2E', color: '#FFFFFF' }}
          >
            <Settings size={18} />
            Espace Admin
          </button>
        </div>
      )}

      {/* Header */}
      <header className="shadow-sm" style={{ background: '#FFFFFF' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/#blog')}
            className="flex items-center gap-2 hover:opacity-80"
            style={{ color: '#335C2E' }}
          >
            <ArrowLeft size={20} />
            Retour à l'accueil
          </button>
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-gray-100"
            style={{ color: '#335C2E' }}
          >
            <Home size={18} />
            Accueil
          </button>
        </div>
      </header>

      {/* Article */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="rounded-lg shadow-lg overflow-hidden" style={{ background: '#FFFFFF' }}>
          {article.image_url && (
            <img
              src={article.image_url}
              alt={article.title}
              className="w-full h-96 object-cover"
            />
          )}
          
          <div className="p-8">
            <h1 className="text-4xl font-bold mb-4" style={{ color: '#335C2E' }}>
              {article.title}
            </h1>

            <div className="flex items-center gap-4 mb-6 text-sm" style={{ color: '#666666' }}>
              <div className="flex items-center gap-1">
                <Calendar size={16} />
                {new Date(article.published_at || article.created_at).toLocaleDateString('fr-CA', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
              <div className="flex items-center gap-1">
                <Eye size={16} />
                {article.views} vues
              </div>
              <div className="flex items-center gap-1">
                <MessageCircle size={16} />
                {approvedComments.length} commentaires
              </div>
            </div>

            {article.tags && article.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {article.tags.map((tag, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 rounded-full text-sm"
                    style={{ background: '#E8F5E9', color: '#2E7D32' }}
                  >
                    <Tag size={14} className="inline mr-1" />
                    {tag}
                  </span>
                ))}
              </div>
            )}

            <div
              className="prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: article.content }}
              style={{ color: '#333333' }}
            />
          </div>
        </div>

        {/* Commentaires */}
        {article.comments_enabled && (
          <div className="mt-8 rounded-lg shadow-lg p-8" style={{ background: '#FFFFFF' }}>
            <h2 className="text-2xl font-bold mb-6" style={{ color: '#335C2E' }}>
              Commentaires ({approvedComments.length})
            </h2>

            {/* Liste des commentaires */}
            <div className="space-y-4 mb-8">
              {approvedComments.map((comment) => (
                <div key={comment.id} className="p-4 rounded-lg" style={{ background: '#F5F5F5' }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold" style={{ color: '#335C2E' }}>
                      {comment.author_name}
                    </span>
                    <span className="text-xs" style={{ color: '#999999' }}>
                      {new Date(comment.created_at).toLocaleDateString('fr-CA')}
                    </span>
                  </div>
                  <p style={{ color: '#333333' }}>{comment.content}</p>
                </div>
              ))}

              {approvedComments.length === 0 && (
                <p className="text-center py-4" style={{ color: '#999999' }}>
                  Aucun commentaire pour le moment. Soyez le premier à commenter !
                </p>
              )}
            </div>

            {/* Formulaire de commentaire */}
            <form onSubmit={handleCommentSubmit} className="space-y-4">
              <h3 className="text-lg font-semibold" style={{ color: '#333333' }}>
                Laisser un commentaire
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Nom *</label>
                  <input
                    type="text"
                    value={commentForm.author_name}
                    onChange={(e) => setCommentForm({ ...commentForm, author_name: e.target.value })}
                    required
                    className="w-full p-3 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email *</label>
                  <input
                    type="email"
                    value={commentForm.author_email}
                    onChange={(e) => setCommentForm({ ...commentForm, author_email: e.target.value })}
                    required
                    className="w-full p-3 border rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Commentaire *</label>
                <textarea
                  value={commentForm.content}
                  onChange={(e) => setCommentForm({ ...commentForm, content: e.target.value })}
                  required
                  rows="4"
                  className="w-full p-3 border rounded-lg"
                  placeholder="Votre commentaire..."
                />
              </div>

              <button
                type="submit"
                disabled={submittingComment}
                className="flex items-center gap-2 px-6 py-3 rounded-lg hover:opacity-90 disabled:opacity-50"
                style={{ background: '#335C2E', color: '#FFFFFF' }}
              >
                <Send size={18} />
                {submittingComment ? 'Envoi...' : 'Publier le commentaire'}
              </button>

              <p className="text-xs" style={{ color: '#666666' }}>
                Votre commentaire sera publié après modération.
              </p>
            </form>
          </div>
        )}
      </article>
    </div>
  );
};

export default BlogArticle;
